import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Building2, Plus, LogOut, Eye, EyeOff, Wallet, TrendingUp, TrendingDown,
  Percent, Target, Settings2, Download, Search, ArrowUpCircle, ArrowDownCircle,
  Trash2, Pencil, Filter,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import { toast } from "sonner";
import api, { eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TransactionModal from "@/components/TransactionModal";
import StructureModal from "@/components/StructureModal";
import BudgetModal from "@/components/BudgetModal";

const PIE_COLORS = ["#EAB308", "#10B981", "#6366F1", "#F43F5E", "#F59E0B", "#06B6D4", "#A855F7", "#EC4899"];
const curMonth = new Date().toISOString().slice(0, 7);

function StatCard({ label, value, icon: Icon, accent, hidden, testid }) {
  return (
    <div className="bg-[#111A2E] border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wider text-slate-400">{label}</span>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <p data-testid={testid} className="mt-2 font-mono text-2xl font-extrabold tracking-tight" style={{ color: accent }}>
        {hidden ? "€ ••••" : value}
      </p>
    </div>
  );
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0E1524] border border-white/15 rounded-lg px-3 py-2 shadow-xl text-xs">
      {label && <p className="text-slate-300 font-medium mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} className="font-mono" style={{ color: p.color || p.fill }}>
          {p.name}: {eur(p.value)}
        </p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [structures, setStructures] = useState([]);
  const [categories, setCategories] = useState({ income: [], expense: [] });
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [budgets, setBudgets] = useState([]);

  const [selStructure, setSelStructure] = useState("all");
  const [hideAmounts, setHideAmounts] = useState(false);

  const [txModal, setTxModal] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [structModal, setStructModal] = useState(false);
  const [budgetModal, setBudgetModal] = useState(false);

  // filters
  const [fType, setFType] = useState("all");
  const [fCategory, setFCategory] = useState("all");
  const [fSearch, setFSearch] = useState("");

  const loadMeta = useCallback(async () => {
    const [s, c] = await Promise.all([api.get("/structures"), api.get("/meta/categories")]);
    setStructures(s.data);
    setCategories(c.data);
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/stats/summary", { params: { structure_id: selStructure } });
      setSummary(data);
    } catch { /* ignore transient errors */ }
  }, [selStructure]);

  const loadTransactions = useCallback(async () => {
    try {
      const { data } = await api.get("/transactions", {
        params: { structure_id: selStructure, type: fType, category: fCategory, search: fSearch || undefined },
      });
      setTransactions(data);
    } catch {
      toast.error("Impossibile caricare le transazioni");
    }
  }, [selStructure, fType, fCategory, fSearch]);

  const loadBudgets = useCallback(async () => {
    try {
      const { data } = await api.get("/budgets", { params: { month: curMonth } });
      setBudgets(data);
    } catch { /* ignore transient errors */ }
  }, []);

  useEffect(() => { loadMeta(); loadBudgets(); }, [loadMeta, loadBudgets]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  const refreshAll = () => { loadSummary(); loadTransactions(); loadBudgets(); loadMeta(); };

  const structName = (id) => structures.find((s) => s.id === id)?.name || "—";
  const structColor = (id) => structures.find((s) => s.id === id)?.color || "#6366F1";

  const deleteTx = async (id) => {
    await api.delete(`/transactions/${id}`);
    toast.success("Transazione eliminata");
    refreshAll();
  };

  const donutData = useMemo(
    () => (summary?.categories || []).filter((c) => c.expense > 0).map((c) => ({ name: c.category, value: c.expense })),
    [summary]
  );

  const monthLabel = (m) => {
    const [y, mo] = m.split("-");
    return new Date(y, mo - 1).toLocaleDateString("it-IT", { month: "short", year: "2-digit" });
  };
  const monthlyData = useMemo(
    () => (summary?.monthly || []).map((m) => ({ ...m, label: monthLabel(m.month) })),
    [summary]
  );

  const overBudgets = budgets.filter((b) => b.spent > b.amount);

  const exportCSV = () => {
    const rows = [["Data", "Tipo", "Struttura", "Categoria", "Descrizione", "Importo"]];
    transactions.forEach((t) =>
      rows.push([t.date, t.type === "income" ? "Entrata" : "Uscita", structName(t.structure_id), t.category, t.description || "", String(t.amount)])
    );
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `neapolis-transazioni-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Esportazione CSV completata");
  };

  return (
    <div className="min-h-screen bg-[#080C14] pb-16">
      {/* ===== STICKY BALANCE BAR ===== */}
      <header className="sticky top-0 z-40 bg-[#0E1524]/90 backdrop-blur-xl border-b border-white/10 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-slate-950" />
              </div>
              <div>
                <p className="font-display font-extrabold text-base leading-none text-slate-50">Neapolis Finance</p>
                <p className="text-[11px] text-slate-500">{user?.name}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <select
                data-testid="structure-selector"
                value={selStructure}
                onChange={(e) => setSelStructure(e.target.value)}
                className="bg-[#0E1524] border border-white/15 text-slate-100 rounded-lg px-3 py-2 text-sm cursor-pointer focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="all">Tutte le strutture</option>
                {structures.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <button
                data-testid="btn-manage-structures"
                onClick={() => setStructModal(true)}
                className="bg-slate-800 hover:bg-slate-700 text-slate-100 p-2 rounded-lg border border-white/10 transition-all"
                title="Gestisci strutture"
              >
                <Settings2 className="w-4 h-4" />
              </button>
              <button
                data-testid="btn-add-transaction"
                onClick={() => { setEditingTx(null); setTxModal(true); }}
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-3.5 py-2 rounded-lg text-sm transition-all active:scale-[0.98] flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" /> Transazione
              </button>
              <button
                data-testid="btn-logout"
                onClick={logout}
                className="bg-slate-800 hover:bg-slate-700 text-slate-300 p-2 rounded-lg border border-white/10 transition-all"
                title="Esci"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* total balance always visible */}
          <div className="mt-3 flex items-center justify-between gap-4 bg-gradient-to-r from-amber-500/10 to-transparent border border-amber-500/20 rounded-xl px-4 py-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-medium uppercase tracking-wider text-amber-300/80">Totale Complessivo</span>
                <button onClick={() => setHideAmounts(!hideAmounts)} className="text-slate-400 hover:text-slate-200">
                  {hideAmounts ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
              <p
                data-testid="total-balance-display"
                className={`mt-1 font-mono text-3xl sm:text-4xl font-extrabold tracking-tight ${
                  (summary?.balance || 0) >= 0 ? "text-emerald-400" : "text-rose-400"
                }`}
              >
                {hideAmounts ? "€ ••••••" : eur(summary?.balance)}
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-6 text-right">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-400">Entrate</p>
                <p data-testid="overall-income-display" className="font-mono text-lg font-bold text-emerald-400">
                  {hideAmounts ? "••••" : eur(summary?.income)}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-400">Uscite</p>
                <p data-testid="overall-expense-display" className="font-mono text-lg font-bold text-rose-400">
                  {hideAmounts ? "••••" : eur(summary?.expense)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ===== CONTENT ===== */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 mt-6">
        {/* stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Bilancio Netto" value={eur(summary?.balance)} icon={Wallet} accent="#EAB308" hidden={hideAmounts} testid="stat-balance" />
          <StatCard label="Totale Entrate" value={eur(summary?.income)} icon={TrendingUp} accent="#34D399" hidden={hideAmounts} testid="stat-income" />
          <StatCard label="Totale Uscite" value={eur(summary?.expense)} icon={TrendingDown} accent="#FB7185" hidden={hideAmounts} testid="stat-expense" />
          <StatCard label="Margine Profitto" value={`${summary?.margin || 0}%`} icon={Percent} accent="#6366F1" hidden={hideAmounts} testid="stat-margin" />
        </div>

        {/* charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="lg:col-span-8 bg-[#111A2E] border border-white/10 rounded-xl p-5">
            <h3 className="font-display text-lg font-semibold text-slate-100">Andamento Entrate vs Uscite</h3>
            <p className="text-xs text-slate-500 mb-4">Ultimi mesi</p>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={monthlyData} margin={{ left: -18, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="inc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F43F5E" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#F43F5E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `€${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="income" name="Entrate" stroke="#10B981" strokeWidth={2} fill="url(#inc)" />
                <Area type="monotone" dataKey="expense" name="Uscite" stroke="#F43F5E" strokeWidth={2} fill="url(#exp)" />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}
            className="lg:col-span-4 bg-[#111A2E] border border-white/10 rounded-xl p-5">
            <h3 className="font-display text-lg font-semibold text-slate-100">Uscite per Categoria</h3>
            <p className="text-xs text-slate-500 mb-2">Ripartizione</p>
            {donutData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {donutData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="#111A2E" strokeWidth={2} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-slate-500 text-center py-16">Nessuna uscita registrata.</p>
            )}
          </motion.div>
        </div>

        {/* per-structure + budget alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
          <div className="lg:col-span-8 bg-[#111A2E] border border-white/10 rounded-xl p-5">
            <h3 className="font-display text-lg font-semibold text-slate-100">Rendimento per Struttura</h3>
            <p className="text-xs text-slate-500 mb-4">Confronto entrate / uscite</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={summary?.per_structure || []} margin={{ left: -18, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="name" stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `€${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="income" name="Entrate" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" name="Uscite" fill="#F43F5E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="lg:col-span-4 bg-[#111A2E] border border-white/10 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-lg font-semibold text-slate-100">Budget del Mese</h3>
                <p className="text-xs text-slate-500">{overBudgets.length > 0 ? `${overBudgets.length} sforati` : "Sotto controllo"}</p>
              </div>
              <button
                data-testid="btn-manage-budgets"
                onClick={() => setBudgetModal(true)}
                className="bg-slate-800 hover:bg-slate-700 text-slate-100 p-2 rounded-lg border border-white/10 transition-all"
              >
                <Target className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              {budgets.slice(0, 4).map((b) => {
                const pct = b.amount > 0 ? Math.min((b.spent / b.amount) * 100, 100) : 0;
                const over = b.spent > b.amount;
                const warn = pct >= 80;
                const barColor = over ? "bg-rose-500" : warn ? "bg-amber-500" : "bg-emerald-500";
                return (
                  <div key={b.id}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-300 truncate pr-2">{b.category}</span>
                      <span className="font-mono text-slate-400 shrink-0">{Math.round(pct)}%</span>
                    </div>
                    <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {budgets.length === 0 && <p className="text-sm text-slate-500 text-center py-6">Nessun budget impostato.</p>}
            </div>
          </div>
        </div>

        {/* transactions */}
        <div className="mt-6 bg-[#111A2E] border border-white/10 rounded-xl p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <h3 className="font-display text-lg font-semibold text-slate-100">Storico Transazioni</h3>
            <button
              data-testid="btn-export-csv"
              onClick={exportCSV}
              className="bg-slate-800 hover:bg-slate-700 text-slate-100 font-medium px-3 py-2 rounded-lg border border-white/10 text-sm transition-all flex items-center gap-1.5"
            >
              <Download className="w-4 h-4" /> Esporta CSV
            </button>
          </div>

          {/* filters */}
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                data-testid="filter-search"
                className="w-full bg-[#0E1524] border border-white/15 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 placeholder:text-slate-500"
                placeholder="Cerca descrizione..."
                value={fSearch}
                onChange={(e) => setFSearch(e.target.value)}
              />
            </div>
            <select
              data-testid="filter-type-select"
              value={fType}
              onChange={(e) => setFType(e.target.value)}
              className="bg-[#0E1524] border border-white/15 rounded-lg px-3 py-2 text-sm cursor-pointer focus:ring-2 focus:ring-amber-500/50"
            >
              <option value="all">Tutti i tipi</option>
              <option value="income">Entrate</option>
              <option value="expense">Uscite</option>
            </select>
            <select
              data-testid="filter-category-select"
              value={fCategory}
              onChange={(e) => setFCategory(e.target.value)}
              className="bg-[#0E1524] border border-white/15 rounded-lg px-3 py-2 text-sm cursor-pointer focus:ring-2 focus:ring-amber-500/50 max-w-[200px]"
            >
              <option value="all">Tutte le categorie</option>
              {[...categories.income, ...categories.expense].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="overflow-x-auto -mx-2" data-testid="transaction-history-table">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/10">
                  <th className="px-2 py-2 font-medium">Data</th>
                  <th className="px-2 py-2 font-medium">Struttura</th>
                  <th className="px-2 py-2 font-medium">Categoria</th>
                  <th className="px-2 py-2 font-medium">Descrizione</th>
                  <th className="px-2 py-2 font-medium text-right">Importo</th>
                  <th className="px-2 py-2 font-medium text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid="transaction-row">
                    <td className="px-2 py-2.5 text-slate-400 font-mono text-xs whitespace-nowrap">
                      {new Date(t.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-2 py-2.5">
                      <span className="inline-flex items-center gap-1.5 text-slate-300">
                        <span className="w-2 h-2 rounded-full" style={{ background: structColor(t.structure_id) }} />
                        {structName(t.structure_id)}
                      </span>
                    </td>
                    <td className="px-2 py-2.5 text-slate-300">{t.category}</td>
                    <td className="px-2 py-2.5 text-slate-500 max-w-[160px] truncate">{t.description || "—"}</td>
                    <td className={`px-2 py-2.5 text-right font-mono font-semibold whitespace-nowrap ${t.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {t.type === "income" ? <ArrowUpCircle className="w-3.5 h-3.5" /> : <ArrowDownCircle className="w-3.5 h-3.5" />}
                        {t.type === "income" ? "+" : "−"}{hideAmounts ? "••••" : eur(t.amount)}
                      </span>
                    </td>
                    <td className="px-2 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => { setEditingTx(t); setTxModal(true); }} className="text-slate-500 hover:text-amber-400 p-1" data-testid={`btn-edit-tx-${t.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteTx(t.id)} className="text-slate-500 hover:text-rose-400 p-1 ml-1" data-testid={`btn-delete-tx-${t.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {transactions.length === 0 && (
              <div className="text-center py-10 text-slate-500 text-sm flex flex-col items-center gap-2">
                <Filter className="w-6 h-6 opacity-50" />
                Nessuna transazione trovata.
              </div>
            )}
          </div>
        </div>
      </main>

      <TransactionModal
        open={txModal}
        onClose={() => setTxModal(false)}
        onSaved={refreshAll}
        structures={structures}
        categories={categories}
        editing={editingTx}
      />
      <StructureModal open={structModal} onClose={() => setStructModal(false)} onSaved={refreshAll} structures={structures} />
      <BudgetModal open={budgetModal} onClose={() => setBudgetModal(false)} onSaved={refreshAll} budgets={budgets} categories={categories} month={curMonth} />
    </div>
  );
}
